import { createContext, ReactNode, useContext } from 'react';
import { TenantBrand, TenantConfig, TenantLocationConfig } from '~/tenants/common/registry';
import { TimezoneContextProvider } from '~/components/hooks/useTz';
import { useParams } from 'react-router';
import TenantWrapper from '~/tenants/common/TenantWrapper';
import { ArrivalHours, DEFAULT_ARRIVAL_HOURS } from '~/lib/arrival-windows';
import type { AlgoliaClientConfig } from '~/components/search/search-key';

export type TenantClientConfig = {
  /**
   * Office search, from the root loader: present for an OFFICE viewer only,
   * and a tenant-scoped secured key that expires, never the parent key
   * (.server/algolia-search.ts). Null when search is off (no
   * ALGOLIA_SEARCH_KEY); absent for everyone else.
   */
  algolia?: AlgoliaClientConfig | null;
  /** The company's "All day" hours, resolved from its settings by the root loader. */
  arrival?: ArrivalHours;
};
type TenantClientContext = TenantConfig & TenantClientConfig;

const context = createContext<TenantClientContext | null>(null);
const brandContext = createContext<TenantBrand | null>(null);
const locationContext = createContext<TenantLocationConfig | null>(null);

export function TenantBrandProvider(props: { brand: TenantBrand; children: ReactNode }) {
  return (
    <brandContext.Provider value={props.brand}>
      {props.children}
    </brandContext.Provider>
  );
}

export function TenantContextProvider(props: { tenant: TenantClientContext; children: ReactNode }) {
  return (
    <context.Provider value={props.tenant}>
      <TimezoneContextProvider timezone={props.tenant.locations[0].timezone}>{props.children}</TimezoneContextProvider>
    </context.Provider>
  );
}

export function TenantLocationProvider(props: { location: TenantLocationConfig | string; children: ReactNode }) {
  const tenant = useTenant();

  const location =
    typeof props.location === 'string' ? tenant.locations.find((l) => l.slug === props.location) : props.location;

  if (!location) {
    throw new Error(`Location ${props.location} could not be found for <TenantLocationProvider />.`);
  }

  return (
    <locationContext.Provider value={location}>
      <TimezoneContextProvider timezone={location.timezone}>{props.children}</TimezoneContextProvider>
    </locationContext.Provider>
  );
}

export function useBrand(): TenantBrand {
  const brand = useContext(brandContext);

  return brand ?? useTenant();
}

export function useTenant() {
  const tenant = useContext(context);

  if (tenant === null) {
    throw new Error('TenantContextProvider not found');
  }

  return tenant;
}

/** What "All day" means to this company's customers (lib/arrival-windows.ts). */
export function useArrivalHours(): ArrivalHours {
  return useTenant().arrival ?? DEFAULT_ARRIVAL_HOURS;
}

export function useTenantWrapper() {
  const tenant = useTenant();

  return new TenantWrapper(tenant);
}

export function usePerformable(id: string) {
  const tenant = useTenantWrapper();

  return tenant.performable(id);
}

export function useTenantLocation(safe = false) {
  const context = useContext(locationContext);

  if (context) {
    return context;
  }

  const tenant = useTenant();
  const params = useParams();

  if (!params.location) {
    if (safe) {
      return tenant.locations[0];
    }

    throw new Error(`Location not found in URL and missing <TenantLocationProvider />.`);
  }

  const location = tenant.locations.find((l) => l.slug === params.location);

  if (!location) {
    throw new Error(`Location ${params.location} not found.`);
  }

  return location;
}
